import { useState, useEffect, useRef, useMemo } from 'react'
import FullCalendar from '@fullcalendar/react'
import timeGridPlugin from '@fullcalendar/timegrid'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import { Video, Building, Home, Trash2, X, Save, Clock, Repeat, Calendar as CalendarIcon, Ban, PhoneCall   } from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../../services/api'

// Configuration des types
const CONSULTATION_TYPES = {
  cabinet: { label: 'Cabinet', icon: Building, color: '#7d9668', bg: '#e8f0e3' },
  video: { label: 'Vidéo', icon: Video, color: '#9333ea', bg: '#f3e8ff' },
  domicile: { label: 'Domicile', icon: Home, color: '#ea580c', bg: '#ffedd5' },
  indisponible: { label: 'Indisponible', icon: Ban, color: '#fa0404ff', bg: '#f3f4f6' },
  telephone: { label: 'Téléphone', icon: PhoneCall, color: '#0d9488', bg: '#ccfbf1' },
}

const BASE_DATE_RECURRING = '2024-01-01'

export default function SlotsPage() {
  const [activeTab, setActiveTab] = useState('recurring')
  
  const [recurringSlots, setRecurringSlots] = useState([])
  const [specificSlots, setSpecificSlots] = useState([])
  
  const [loading, setLoading] = useState(false)
  const calendarRef = useRef(null)

  const [modalOpen, setModalOpen] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [formData, setFormData] = useState({
    id: null,
    date: null,
    day_of_week: 0,
    start_time: '',
    end_time: '',
    consultation_type: 'cabinet',
    duration_minutes: 60
  })

  // --- Data Fetching ---

  const fetchRecurringSlots = () => {
    setLoading(true)
    api.get('/api/slots/')
      .then(res => setRecurringSlots(res.data))
      .catch(console.error)
      .finally(() => setLoading(false))
  }

  const fetchSpecificSlots = (start, end) => {
    api.get('/api/slots/specific', { params: { start, end } })
      .then(res => setSpecificSlots(res.data))
      .catch(console.error)
  }

  useEffect(() => {
    fetchRecurringSlots()
  }, [])

  // --- Helpers ---
  const formatTime = (date) => date.toTimeString().split(' ')[0]
  const formatDate = (date) => date.toISOString().split('T')[0]

  // --- Event Generators ---

  const mapRecurringForWeekView = (slot) => {
    const date = new Date(BASE_DATE_RECURRING)
    date.setDate(date.getDate() + slot.day_of_week)
    const dateStr = formatDate(date)
    return {
      id: `rec-${slot.id}`,
      title: CONSULTATION_TYPES[slot.consultation_type].label,
      start: `${dateStr}T${slot.start_time}`,
      end: `${dateStr}T${slot.end_time}`,
      backgroundColor: CONSULTATION_TYPES[slot.consultation_type].color,
      borderColor: CONSULTATION_TYPES[slot.consultation_type].color,
      extendedProps: { source: 'recurring', data: slot }
    }
  }

  const mapRecurringGhost = (slot) => {
    const fcDay = (slot.day_of_week + 1) % 7 
    const typeConf = CONSULTATION_TYPES[slot.consultation_type]
    
    return {
      id: `ghost-${slot.id}`,
      // IMPORTANT : On supprime display: 'background' pour qu'il s'affiche en vue Mois
      // On met pointer-events-none pour qu'il ne bloque pas la souris en vue Semaine
      classNames: ['ghost-event', 'pointer-events-none'], 
      startTime: slot.start_time,
      endTime: slot.end_time,
      daysOfWeek: [fcDay],
      
      backgroundColor: 'transparent', // On gérera la couleur dans le render
      borderColor: 'transparent',
      
      extendedProps: { 
        source: 'ghost',
        type: slot.consultation_type,
        sortIdx: 1 // Pour forcer l'affichage derrière les spécifiques
      }
    }
  }

  const mapSpecificSlot = (slot) => {
    return {
      id: `spec-${slot.id}`,
      title: CONSULTATION_TYPES[slot.consultation_type].label,
      start: `${slot.date}T${slot.start_time}`,
      end: `${slot.date}T${slot.end_time}`,
      // Les spécifiques sont bien opaques et solides
      backgroundColor: CONSULTATION_TYPES[slot.consultation_type].color,
      borderColor: 'white',
      borderWidth: 2,
      extendedProps: { source: 'specific', data: slot, sortIdx: 2 }
    }
  }

  const calendarEvents = useMemo(() => {
    if (activeTab === 'recurring') {
      return recurringSlots.map(mapRecurringForWeekView)
    } else {
      const ghosts = recurringSlots.map(mapRecurringGhost)
      const specifics = specificSlots.map(mapSpecificSlot)
      return [...ghosts, ...specifics]
    }
  }, [activeTab, recurringSlots, specificSlots])

  // --- Handlers ---

  const handleDatesSet = (dateInfo) => {
    if (activeTab === 'specific') {
      fetchSpecificSlots(formatDate(dateInfo.start), formatDate(dateInfo.end))
    }
  }

  const handleDateClick = (info) => {
    if (activeTab === 'specific' && info.view.type === 'dayGridMonth') {
      const calendarApi = calendarRef.current.getApi()
      calendarApi.changeView('timeGridWeek', info.dateStr)
    }
  }

  const handleSelect = (selectInfo) => {
    if (selectInfo.view.type === 'dayGridMonth') {
        const calendarApi = calendarRef.current.getApi()
        calendarApi.changeView('timeGridWeek', selectInfo.startStr)
        return
    }

    setEditMode(false)
    const startStr = formatTime(selectInfo.start)
    const endStr = formatTime(selectInfo.end)

    if (activeTab === 'recurring') {
      setFormData({
        id: null,
        day_of_week: selectInfo.start.getDay() === 0 ? 6 : selectInfo.start.getDay() - 1,
        start_time: startStr,
        end_time: endStr,
        consultation_type: 'cabinet'
      })
    } else {
      setFormData({
        id: null,
        date: formatDate(selectInfo.start),
        start_time: startStr,
        end_time: endStr,
        consultation_type: 'cabinet'
      })
    }
    setModalOpen(true)
  }

  const handleEventClick = (clickInfo) => {
    const props = clickInfo.event.extendedProps
    if (props.source === 'ghost') return // Ignore les clics sur fantômes

    const slotData = props.data
    const typeLabel = CONSULTATION_TYPES[slotData.consultation_type]?.label || slotData.consultation_type
    const start = slotData.start_time.slice(0, 5)
    const end = slotData.end_time.slice(0, 5)

    if (activeTab === 'specific') {
        if (clickInfo.view.type === 'dayGridMonth') {
            const calendarApi = calendarRef.current.getApi()
            calendarApi.changeView('timeGridWeek', clickInfo.event.startStr)
            return
        }
        if (confirm(`Supprimer ce créneau ponctuel ?\n\n📅 Type : ${typeLabel}\n🕒 Horaire : de ${start} à ${end}`)) {

            handleDeleteSpecific(slotData.id)
        }
        return
    }
    
    setEditMode(true)
    setFormData({
      id: slotData.id,
      day_of_week: slotData.day_of_week,
      start_time: slotData.start_time,
      end_time: slotData.end_time,
      consultation_type: slotData.consultation_type
    })
    setModalOpen(true)
  }

  const handleEventChange = async (changeInfo) => {
    const props = changeInfo.event.extendedProps
    if (props.source !== 'recurring') {
      toast.error("Déplacement impossible ici")
      changeInfo.revert()
      return
    }
    const event = changeInfo.event
    const slotId = props.data.id
    const newDay = event.start.getDay() === 0 ? 6 : event.start.getDay() - 1
    
    try {
      await api.patch(`/api/slots/${slotId}`, {
        day_of_week: newDay,
        start_time: formatTime(event.start),
        end_time: formatTime(event.end)
      })
      toast.success('Créneau modifié')
      fetchRecurringSlots()
    } catch (error) {
      toast.error("Impossible de modifier")
      changeInfo.revert()
    }
  }

  // --- API Actions ---

  const handleSave = async () => {
    try {
      const start = formData.start_time.length === 5 ? formData.start_time + ':00' : formData.start_time
      const end = formData.end_time.length === 5 ? formData.end_time + ':00' : formData.end_time

      if (activeTab === 'recurring') {
        const payload = { ...formData, start_time: start, end_time: end }
        if (editMode) {
          await api.patch(`/api/slots/${formData.id}`, payload)
        } else {
          await api.post('/api/slots/', payload)
        }
        fetchRecurringSlots()
      } else {
        const payload = { date: formData.date, start_time: start, end_time: end, consultation_type: formData.consultation_type }
        await api.post('/api/slots/specific', payload)
        const calendarApi = calendarRef.current.getApi()
        fetchSpecificSlots(formatDate(calendarApi.view.activeStart), formatDate(calendarApi.view.activeEnd))
      }
      toast.success('Enregistré')
      setModalOpen(false)
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur')
    }
  }

  const handleDelete = async () => {
    if (!confirm('Supprimer ?')) return
    try {
      await api.delete(`/api/slots/${formData.id}`)
      toast.success('Supprimé')
      setModalOpen(false)
      fetchRecurringSlots()
    } catch (error) { toast.error('Erreur') }
  }

  const handleDeleteSpecific = async (id) => {
    try {
      await api.delete(`/api/slots/specific/${id}`)
      toast.success('Supprimé')
      const calendarApi = calendarRef.current.getApi()
      fetchSpecificSlots(formatDate(calendarApi.view.activeStart), formatDate(calendarApi.view.activeEnd))
    } catch (error) { toast.error('Erreur') }
  }

  // --- RENDER CUSTOM ---
  const renderEventContent = (eventInfo) => {
    const props = eventInfo.event.extendedProps
    const type = props.type || (props.data && props.data.consultation_type) || 'cabinet'
    const Icon = CONSULTATION_TYPES[type]?.icon || Building
    const isGhost = props.source === 'ghost'
    const config = CONSULTATION_TYPES[type]

    // VUE MOIS
    if (eventInfo.view.type === 'dayGridMonth') {
        return (
            <div 
                className={`flex items-center w-full h-full px-1 py-0.5 rounded-sm`}
                // Fond coloré mais léger pour les fantômes, Normal pour les spécifiques
                style={{ backgroundColor: isGhost ? config.bg : config.color }}
            >
                 {/* Point coloré pour ghost, Blanc pour spécifique */}
                 <div className="w-1.5 h-1.5 rounded-full mr-1 shrink-0" 
                      style={{ backgroundColor: isGhost ? config.color : 'white' }}></div>
                 
                 <span className={`text-[10px] truncate font-medium ${isGhost ? 'text-gray-600' : 'text-white'}`}>
                    {/* Toujours afficher le label, même pour les fantômes */}
                    {isGhost ? `(Réc.) ${config.label}` : `${eventInfo.timeText.replace(':', 'h')} ${config.label}`}
                 </span>
            </div>
        )
    }

    // VUE SEMAINE
    return (
      <div 
        className={`flex items-center gap-1 px-1 overflow-hidden h-full ${isGhost ? 'opacity-70 border border-dashed' : 'shadow-sm'}`}
        style={{ 
            backgroundColor: isGhost ? config.bg : config.color,
            borderColor: isGhost ? config.color : 'transparent',
            // Texte foncé sur fond light (ghost), Texte blanc sur fond foncé (specific)
            color: isGhost ? config.color : 'white'
        }}
      >
        <Icon className="w-3 h-3 flex-shrink-0" />
        <span className="text-xs font-semibold truncate">
          {activeTab === 'recurring' 
            ? eventInfo.timeText 
            : (isGhost ? `(Réc) ${config.label}` : eventInfo.event.title)
          }
        </span>
      </div>
    )
  }

  return (
    <div className="h-[calc(100vh-100px)] flex flex-col">
      <div className="mb-4 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div className="flex items-center gap-6">
            <div>
                <h1 className="text-2xl font-serif text-gray-800">Disponibilités</h1>
                <p className="text-gray-500">Gérez vos horaires d'ouverture.</p>
            </div>
            
            <div className="flex bg-gray-100 p-1 rounded-xl">
                <button
                    onClick={() => setActiveTab('recurring')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        activeTab === 'recurring' ? 'bg-white text-sage-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                    <Repeat className="w-4 h-4" />
                    Semaine Type
                </button>
                <button
                    onClick={() => setActiveTab('specific')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        activeTab === 'specific' ? 'bg-white text-sage-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                    <CalendarIcon className="w-4 h-4" />
                    Calendrier
                </button>
            </div>
        </div>
        
        <div className="hidden sm:flex gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm">
            {Object.entries(CONSULTATION_TYPES).map(([key, conf]) => (
                <div key={key} className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: conf.color }} />
                    <span className="text-xs text-gray-600 font-medium">{conf.label}</span>
                </div>
            ))}
        </div>
      </div>

      <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 p-4 overflow-hidden relative">
        {loading && <div className="absolute inset-0 bg-white/50 z-10 flex items-center justify-center"><div className="spinner" /></div>}
        
        <FullCalendar
          ref={calendarRef}
          key={activeTab}
          plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin]}
          eventOrder="sortIdx"
          initialView={activeTab === 'recurring' ? 'timeGridWeek' : 'dayGridMonth'}
          initialDate={activeTab === 'recurring' ? BASE_DATE_RECURRING : new Date().toISOString().split('T')[0]}

          headerToolbar={activeTab === 'recurring' 
            ? false 
            : { left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek' }
          }
          
          dayHeaderFormat={activeTab === 'recurring'
            ? { weekday: 'long' }
            : { weekday: 'short', day: 'numeric', omitCommas: true }
          }
          
          locale="fr"
          firstDay={1}
          allDaySlot={false}
          slotMinTime="06:00:00"
          slotMaxTime="22:00:00"
          height="100%"
          slotLabelFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
          navLinks={true}
          
          selectable={true}
          selectMirror={true}
          editable={activeTab === 'recurring'}
          eventOverlap={false}
          
          select={handleSelect}
          eventClick={handleEventClick}
          dateClick={handleDateClick}
          eventResize={handleEventChange}
          eventDrop={handleEventChange}
          datesSet={handleDatesSet}
          selectLongPressDelay={500} // <-- La magie mobile
          selectAllow={(selectInfo) => {
             const start = selectInfo.start;
             const end = new Date(selectInfo.end);
             if (end.getHours() === 0 && end.getMinutes() === 0) end.setSeconds(end.getSeconds() - 1);
             return start.toDateString() === end.toDateString();
          }}
          
          events={calendarEvents}
          eventContent={renderEventContent}
        />
      </div>

      {/* MODAL (inchangée, copie-la de ta version précédente ou garde celle-ci) */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-xl transform transition-all scale-100">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-serif font-bold text-gray-800">
                {activeTab === 'recurring' 
                    ? (editMode ? 'Modifier Semaine Type' : 'Ajout Semaine Type') 
                    : 'Ajout Ponctuel'
                }
              </h3>
              <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            
            {activeTab === 'specific' && (
                <div className="mb-4 text-center">
                    <p className="text-sm font-medium text-sage-700 bg-sage-50 py-1 rounded-lg">
                        Date : {new Date(formData.date).toLocaleDateString()}
                    </p>
                </div>
            )}

            <div className="flex gap-4 mb-6 bg-gray-50 p-3 rounded-xl">
              <div className="flex-1">
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Début</label>
                <div className="relative">
                  <input type="time" value={formData.start_time.slice(0, 5)} onChange={e => setFormData({...formData, start_time: e.target.value})} className="w-full bg-white border-gray-200 pl-8 rounded-md" />
                  <Clock className="w-4 h-4 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
                </div>
              </div>
              <div className="flex-1">
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Fin</label>
                <div className="relative">
                  <input type="time" value={formData.end_time.slice(0, 5)} onChange={e => setFormData({...formData, end_time: e.target.value})} className="w-full bg-white border-gray-200 pl-8 rounded-md" />
                  <Clock className="w-4 h-4 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
                </div>
              </div>
            </div>
            {/* SÉLECTEUR DE DURÉE (uniquement pour les ponctuels) */}
            {activeTab === 'specific' && (
              <div className="mb-6 bg-gray-50 p-3 rounded-xl">
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Durée des créneaux</label>
                <select
                  value={formData.duration_minutes}
                  onChange={e => setFormData({...formData, duration_minutes: parseInt(e.target.value)})}
                  className="w-full bg-white border-gray-200 rounded-md"
                >
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60} selected>1 heure</option>
                  <option value={90}>1 heure 30</option>
                  <option value={120}>2 heures</option>
                </select>
                <p className="text-xs text-gray-400 mt-1">
                  Durée de chaque RDV si un patient choisit ce créneau.
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 gap-3 mb-6">
              {Object.entries(CONSULTATION_TYPES).map(([key, conf]) => (
                <button key={key} onClick={() => setFormData({...formData, consultation_type: key})} className={`flex items-center gap-4 p-3 rounded-xl border-2 transition-all ${formData.consultation_type === key ? `border-[${conf.color}] bg-gray-50 ring-1 ring-[${conf.color}]` : 'border-gray-100 hover:border-gray-200'}`} style={{ borderColor: formData.consultation_type === key ? conf.color : '' }}>
                  <div className={`p-2 rounded-lg`} style={{ backgroundColor: conf.bg }}><conf.icon className="w-5 h-5" style={{ color: conf.color }} /></div>
                  <span className="font-medium text-gray-700">{conf.label}</span>
                </button>
              ))}
            </div>

            <div className="flex gap-3">
              {editMode && activeTab === 'recurring' && (
                <button onClick={handleDelete} className="btn btn-secondary text-red-600 hover:bg-red-50 p-3"><Trash2 className="w-5 h-5" /></button>
              )}
              <button onClick={handleSave} className="btn btn-primary flex-1 py-3 text-lg flex items-center justify-center gap-2"><Save className="w-5 h-5" />{editMode ? 'Enregistrer' : 'Créer'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}